import { z } from "zod";
import { adminOperatorIdSchema, adminReasonCodeSchema, adminUlidSchema, } from "./admin-operations-core.js";
import { customerOperationsOpaqueIdSchema } from "./customer-operations-core.js";
import { customerSupportTicketMessageSchema, supportTicketCustomerStateSchema, supportTicketIdempotencyKeySchema, supportTicketMessageSchema, } from "./customer-operations-support.js";
import { adminSupportTicketDetailSchema } from "./admin-operations-investigations.js";
/**
 * T3b shared-ticket protocol. Versioned requests use the shared adapter; unversioned clients
 * continue using adminSupportTransitionCommandSchema during migration.
 * The server derives the actor, customer, book and internal audit case from authentication and
 * the verified ticket. None of those can be supplied as authority in these commands.
 */
export const adminSupportWorkspaceSchemaVersionSchema = z.literal("2026-10-26.1");
export const adminSupportWorkspaceActionSchema = z.enum([
    "TAKE_OVER",
    "HAND_OFF",
    "REPLY",
    "ADD_STAFF_NOTE",
    "ASSIGN_SELF",
    "START_REVIEW",
    "WAIT_FOR_CUSTOMER",
    "RESOLVE",
    "REOPEN_REVIEW",
]);
const commandFields = {
    schemaVersion: adminSupportWorkspaceSchemaVersionSchema,
    ticketId: customerOperationsOpaqueIdSchema,
    // Created once per user action and reused on an uncertain retry, including after handoff.
    commandId: supportTicketIdempotencyKeySchema,
    expectedVersion: z.number().int().positive(),
    // Adapters supply the action's governed reason code; this is not an extra customer form.
    reasonCode: adminReasonCodeSchema,
};
const handoffNoteSchema = supportTicketMessageSchema.max(1_000);
export const adminSupportTakeOverCommandSchema = z
    .object({
    ...commandFields,
    action: z.literal("TAKE_OVER"),
    staffNote: handoffNoteSchema.optional(),
})
    .strict();
export const adminSupportHandOffCommandSchema = z
    .object({
    ...commandFields,
    action: z.literal("HAND_OFF"),
    targetOperatorId: adminOperatorIdSchema,
    staffNote: handoffNoteSchema.optional(),
})
    .strict();
export const adminSupportReplyCommandSchema = z
    .object({
    ...commandFields,
    action: z.literal("REPLY"),
    customerMessage: supportTicketMessageSchema,
    // Only this authenticated author's exact revision/text may be cleared after confirmation.
    draftVersion: z.number().int().positive().optional(),
})
    .strict();
export const adminSupportAddStaffNoteCommandSchema = z
    .object({
    ...commandFields,
    action: z.literal("ADD_STAFF_NOTE"),
    staffNote: supportTicketMessageSchema,
})
    .strict();
export const adminSupportWorkspaceCommandSchema = z.discriminatedUnion("action", [
    adminSupportTakeOverCommandSchema,
    adminSupportHandOffCommandSchema,
    adminSupportReplyCommandSchema,
    adminSupportAddStaffNoteCommandSchema,
    z.object({ ...commandFields, action: z.literal("ASSIGN_SELF") }).strict(),
    z.object({ ...commandFields, action: z.literal("START_REVIEW") }).strict(),
    z.object({ ...commandFields, action: z.literal("REOPEN_REVIEW") }).strict(),
    z
        .object({
        ...commandFields,
        action: z.literal("WAIT_FOR_CUSTOMER"),
        customerMessage: supportTicketMessageSchema,
    })
        .strict(),
    z
        .object({
        ...commandFields,
        action: z.literal("RESOLVE"),
        customerMessage: supportTicketMessageSchema,
    })
        .strict(),
]);
/** Provider acceptance is distinct from observed delivery. Never translate a timeout to success. */
export const adminSupportReplyNotificationSchema = z.discriminatedUnion("state", [
    z.object({ state: z.literal("PENDING") }).strict(),
    z.object({ state: z.literal("ACCEPTED"), acceptedAt: z.string().datetime() }).strict(),
    z.object({ state: z.literal("DELIVERED"), deliveredAt: z.string().datetime() }).strict(),
    z.object({ state: z.literal("NEEDS_ATTENTION") }).strict(),
    z.object({ state: z.literal("UNKNOWN") }).strict(),
    z.object({ state: z.literal("SUPERSEDED") }).strict(),
]);
const receiptFields = {
    schemaVersion: adminSupportWorkspaceSchemaVersionSchema,
    ticketId: customerOperationsOpaqueIdSchema,
    commandId: supportTicketIdempotencyKeySchema,
    // A replay returns this original receipt, including its original operator and version.
    performedBy: adminOperatorIdSchema,
    reasonCode: adminReasonCodeSchema,
    recordedAt: z.string().datetime(),
    auditEventId: adminUlidSchema,
    caseId: adminUlidSchema.optional(),
    previousVersion: z.number().int().positive(),
    version: z.number().int().positive(),
};
const openStateSchema = supportTicketCustomerStateSchema.exclude(["RESOLVED"]);
export const adminSupportWorkspaceReceiptSchema = z
    .discriminatedUnion("action", [
    z
        .object({
        ...receiptFields,
        action: z.literal("TAKE_OVER"),
        state: openStateSchema,
        previousAssignedOperatorId: adminOperatorIdSchema,
        assignedOperatorId: adminOperatorIdSchema,
        staffNoteId: customerOperationsOpaqueIdSchema.optional(),
    })
        .strict(),
    z
        .object({
        ...receiptFields,
        action: z.literal("HAND_OFF"),
        state: openStateSchema,
        previousAssignedOperatorId: adminOperatorIdSchema,
        assignedOperatorId: adminOperatorIdSchema,
        staffNoteId: customerOperationsOpaqueIdSchema.optional(),
    })
        .strict(),
    z
        .object({
        ...receiptFields,
        action: z.literal("REPLY"),
        state: z.literal("IN_REVIEW"),
        messageId: customerOperationsOpaqueIdSchema,
        visibility: z.literal("CUSTOMER"),
        notification: adminSupportReplyNotificationSchema,
    })
        .strict(),
    z
        .object({
        ...receiptFields,
        action: z.literal("ADD_STAFF_NOTE"),
        state: openStateSchema,
        noteId: customerOperationsOpaqueIdSchema,
        visibility: z.literal("STAFF"),
    })
        .strict(),
    z
        .object({
        ...receiptFields,
        action: z.literal("ASSIGN_SELF"),
        state: z.literal("IN_REVIEW"),
        assignedOperatorId: adminOperatorIdSchema,
    })
        .strict(),
    z
        .object({
        ...receiptFields,
        action: z.literal("START_REVIEW"),
        state: z.literal("IN_REVIEW"),
        assignedOperatorId: adminOperatorIdSchema,
    })
        .strict(),
    z
        .object({
        ...receiptFields,
        action: z.literal("REOPEN_REVIEW"),
        state: z.literal("IN_REVIEW"),
        assignedOperatorId: adminOperatorIdSchema,
    })
        .strict(),
    z
        .object({
        ...receiptFields,
        action: z.literal("WAIT_FOR_CUSTOMER"),
        state: z.literal("WAITING_FOR_YOU"),
        messageId: customerOperationsOpaqueIdSchema,
        visibility: z.literal("CUSTOMER"),
        notification: adminSupportReplyNotificationSchema,
    })
        .strict(),
    z
        .object({
        ...receiptFields,
        action: z.literal("RESOLVE"),
        state: z.literal("RESOLVED"),
        messageId: customerOperationsOpaqueIdSchema,
        visibility: z.literal("CUSTOMER"),
        notification: adminSupportReplyNotificationSchema,
    })
        .strict(),
])
    .superRefine((receipt, context) => {
    if (receipt.version !== receipt.previousVersion + 1)
        context.addIssue({
            code: "custom",
            path: ["version"],
            message: "A successful command records exactly one ticket version change.",
        });
    if (receipt.action === "TAKE_OVER" || receipt.action === "HAND_OFF") {
        if (receipt.assignedOperatorId === receipt.previousAssignedOperatorId)
            context.addIssue({
                code: "custom",
                path: ["assignedOperatorId"],
                message: "The new handler must differ from the previous handler.",
            });
        if (receipt.action === "TAKE_OVER" && receipt.assignedOperatorId !== receipt.performedBy)
            context.addIssue({
                code: "custom",
                path: ["assignedOperatorId"],
                message: "A takeover assigns the request to the authenticated operator.",
            });
        if (receipt.action === "HAND_OFF" &&
            receipt.previousAssignedOperatorId !== receipt.performedBy)
            context.addIssue({
                code: "custom",
                path: ["previousAssignedOperatorId"],
                message: "The current handler performs a named handoff.",
            });
    }
});
export const adminSupportWorkspaceResponseSchema = z
    .object({
    data: adminSupportWorkspaceReceiptSchema,
    notification: adminSupportReplyNotificationSchema.optional(),
    draftCleanup: z.enum(["CLEARED", "KEPT", "UNKNOWN"]).optional(),
})
    .strict();
const beforeSchema = z
    .string()
    .max(180)
    .regex(/^(?:MESSAGE|STAFF_NOTE)#\d{4}-\d{2}-\d{2}T[0-9:.]+Z#[0-9A-HJKMNP-TV-Z]{26}$/u);
export const adminSupportWorkspaceReadCommandSchema = z
    .object({
    schemaVersion: adminSupportWorkspaceSchemaVersionSchema,
    ticketId: customerOperationsOpaqueIdSchema,
    limit: z.number().int().positive().max(100).default(50),
    messageBefore: beforeSchema.refine((value) => value.startsWith("MESSAGE#")).optional(),
    staffBefore: beforeSchema.refine((value) => value.startsWith("STAFF_NOTE#")).optional(),
})
    .strict();
export const adminSupportWorkspaceReadResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminSupportWorkspaceSchemaVersionSchema,
        ticket: adminSupportTicketDetailSchema,
        messages: z.array(customerSupportTicketMessageSchema).max(100),
        staffNotes: z
            .array(z
            .object({
            noteId: customerOperationsOpaqueIdSchema,
            authorId: adminOperatorIdSchema,
            message: supportTicketMessageSchema,
            createdAt: z.string().datetime(),
            visibility: z.literal("STAFF"),
        })
            .strict())
            .max(100),
        nextMessageBefore: beforeSchema.optional(),
        nextStaffBefore: beforeSchema.optional(),
        /**
         * The request's own work record, only for the person handling the open request: the
         * request-bound case its make-it-right, book-diagnostics, erasure and account-history
         * tools are recorded under (stage 2). Absent until the request's first update.
         */
        requestCaseId: adminUlidSchema.optional(),
    })
        .strict(),
})
    .strict();
/** HTTP 409 projection: enough to refresh coordination without echoing private drafts or notes. */
export const adminSupportWorkspaceConflictResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminSupportWorkspaceSchemaVersionSchema,
        commandId: supportTicketIdempotencyKeySchema,
        ticketId: customerOperationsOpaqueIdSchema,
        outcome: z.literal("VERSION_CONFLICT"),
        current: z
            .object({
            state: supportTicketCustomerStateSchema,
            version: z.number().int().positive(),
            assignedOperatorId: adminOperatorIdSchema.optional(),
        })
            .strict(),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=admin-operations-support-workspace.js.map